import * as React from 'react';

type Props = {
  title: string;
  icon?: React.ReactNode;            // colored icon left of title (e.g. blue grid for Module)
  iconVariant?: 'blue' | 'green' | 'orange';  // icon background color variant
  defaultOpen?: boolean;
  badge?: React.ReactNode;           // e.g. "3 required" red badge for Advanced section
  badgeCount?: number;               // shorthand — renders "{N} required" when > 0
  summarySlot?: React.ReactNode;     // shown when COLLAPSED (e.g. "Canadian Solar · 40.9×67.1 in")
  summaryPosition?: 'inline' | 'strip'; // 'inline' = in header row (Location), 'strip' = below header (Module)
  infoBanner?: React.ReactNode;      // rendered below header when open (e.g. "Auto-filled from Step 2 address")
  showMoreLabel?: string;            // label for "Show N more fields" pill (e.g. "Show 2 more fields")
  showMoreVisible?: boolean;         // controls pill visibility
  onShowMoreClick?: () => void;      // handler for pill click
  forceOpenSignal?: number;          // increment to force-open from outside
  children: React.ReactNode;
};

type State = {
  open: boolean;
};

/**
 * V3CollapsibleSection — accordion section in the left panel.
 *
 * Each section (Module, Location, Advanced) is one of these.
 * Click the header to toggle open/collapsed. When collapsed,
 * only the header row is visible.
 *
 * Features matching Heni V5 design:
 *  - `badge` / `badgeCount`: red "N required" indicator on
 *    Advanced section. updateSectionBadges() in Heni's design
 *    counts 4 fields: Building Height, Shortest Bldg Length,
 *    Mid Clamps, End Clamps.
 *  - `infoBanner`: "Auto-filled from Step 2 address · Edit
 *    any field directly" below Location header.
 *  - `showMoreLabel` / `onShowMoreClick`: "Show N more fields"
 *    pill that reveals optional fields (Weight, Area in Module;
 *    full location fields in Location).
 */
class V3CollapsibleSection extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { open: props.defaultOpen !== undefined ? props.defaultOpen : true };
  }

  toggle = () => {
    this.setState((prev) => ({ open: !prev.open }));
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.forceOpenSignal !== this.props.forceOpenSignal && this.props.forceOpenSignal) {
      this.setState({ open: true });
    }
  }

  render() {
    const {
      title, icon, iconVariant, badge, badgeCount, summarySlot, summaryPosition = 'strip',
      infoBanner, showMoreLabel, showMoreVisible, onShowMoreClick,
      children,
    } = this.props;
    const { open } = this.state;

    const renderedBadge = badge || (badgeCount && badgeCount > 0 ? (
      <span className="v3-section-badge required">{badgeCount} required</span>
    ) : null);

    return (
      <div className={`v3-collapsible ${open ? 'open' : ''}`}>
        <div className={`v3-collapsible-header${!open && summarySlot && summaryPosition === 'inline' ? ' has-inline-summary' : ''}`} onClick={this.toggle}>
          {icon && (
            <div className={`v3-lp-section-icon ${iconVariant || ''}`}>{icon}</div>
          )}
          <span className="v3-collapsible-title">{title}</span>
          {!open && summarySlot && summaryPosition === 'inline' && (
            <span className="v3-collapsible-inline-summary">{summarySlot}</span>
          )}
          {renderedBadge && <span className="v3-collapsible-badge">{renderedBadge}</span>}
          <svg
            className="v3-collapsible-chevron"
            width="12" height="12" viewBox="0 0 12 12" fill="none"
          >
            <path
              d={open ? 'M2 8l4-4 4 4' : 'M2 4l4 4 4-4'}
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
        </div>
        {!open && summarySlot && summaryPosition === 'strip' && (
          <div className="v3-collapsible-summary-strip" onClick={this.toggle}>{summarySlot}</div>
        )}
        {open && (
          <div className="v3-collapsible-body">
            {infoBanner && <div className="v3-section-info-banner">{infoBanner}</div>}
            {children}
            {showMoreVisible && showMoreLabel && (
              <button
                type="button"
                className="v3-show-more-pill"
                onClick={onShowMoreClick}
              >
                {showMoreLabel}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default V3CollapsibleSection;
